import { useEffect, useRef, useState, type ReactNode } from "react";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { TextInput } from "@/components/ui/Field";
import { Spinner } from "@/components/ui/Spinner";
import { LieferantSearchField } from "@/components/LieferantSearchField";
import { ApiError } from "@/lib/apiClient";
import { toast } from "@/lib/toast";
import {
  bulkUpdateArtikel,
  createArtikelBulkJob,
  getArtikelBulkJob,
  type ArtikelBulkFilterPayload,
  type ArtikelBulkJob,
  type ArtikelUpdatePayload,
} from "@/lib/artikel";
import { getZuschlagRegeln, type ZuschlagRegel } from "@/lib/artikelZuschlag";
import { UnitAutocompleteField } from "@/pages/dokumente/UnitAutocompleteField";

function parseNum(raw: string): number | undefined {
  const s = raw.trim().replace(/\s/g, "").replace(",", ".");
  if (!s) return undefined;
  const n = Number(s);
  return Number.isFinite(n) ? n : undefined;
}

const SYNC_ID_LIMIT = 500;

type FieldKey =
  | "name"
  | "beschreibung"
  | "einheit"
  | "ean"
  | "matchcode"
  | "hersteller"
  | "hersteller_nr"
  | "warengruppe"
  | "lieferant"
  | "preis_brutto"
  | "rabatt"
  | "preis_netto"
  | "preismenge"
  | "preis_einheit"
  | "preis_gueltig_bis"
  | "aufschlag"
  | "verkaufspreis"
  | "zuschlag"
  | "import_geschuetzt"
  | "preis_geschuetzt";

export type ArtikelBulkSelection = {
  mode: "ids" | "allMatching";
  ids: number[];
  excludeIds: number[];
  total: number;
  filter: ArtikelBulkFilterPayload;
};

type ZuschlagModus = "regel" | "manuell_prozent" | "manuell_vk";

function OptField({
  checked,
  onToggle,
  children,
}: {
  checked: boolean;
  onToggle: (v: boolean) => void;
  children: ReactNode;
}) {
  return (
    <label className="flex items-start gap-2 text-sm">
      <input
        type="checkbox"
        className="mt-1"
        checked={checked}
        onChange={(e) => onToggle(e.target.checked)}
      />
      <span className="min-w-0 flex-1">{children}</span>
    </label>
  );
}

export function ArtikelBulkEditDialog({
  open,
  selection,
  onClose,
  onSaved,
  intent = "edit",
}: {
  open: boolean;
  selection: ArtikelBulkSelection;
  onClose: () => void;
  onSaved: () => void;
  /** „delete“ blendet nur aus, ohne Stammdaten zu ändern. */
  intent?: "edit" | "delete";
}) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [job, setJob] = useState<ArtikelBulkJob | null>(null);
  const [regeln, setRegeln] = useState<ZuschlagRegel[]>([]);
  const [apply, setApply] = useState<Record<FieldKey, boolean>>({
    name: false,
    beschreibung: false,
    einheit: false,
    ean: false,
    matchcode: false,
    hersteller: false,
    hersteller_nr: false,
    warengruppe: false,
    lieferant: false,
    preis_brutto: false,
    rabatt: false,
    preis_netto: false,
    preismenge: false,
    preis_einheit: false,
    preis_gueltig_bis: false,
    aufschlag: false,
    verkaufspreis: false,
    zuschlag: false,
    import_geschuetzt: false,
    preis_geschuetzt: false,
  });
  const [name, setName] = useState("");
  const [beschreibung, setBeschreibung] = useState("");
  const [einheit, setEinheit] = useState("");
  const [ean, setEan] = useState("");
  const [matchcode, setMatchcode] = useState("");
  const [hersteller, setHersteller] = useState("");
  const [herstellerNr, setHerstellerNr] = useState("");
  const [warengruppe, setWarengruppe] = useState("");
  const [lieferantName, setLieferantName] = useState("");
  const [lieferantGpId, setLieferantGpId] = useState<number | null>(null);
  const [preisBrutto, setPreisBrutto] = useState("");
  const [rabatt, setRabatt] = useState("");
  const [preisNetto, setPreisNetto] = useState("");
  const [preismenge, setPreismenge] = useState("");
  const [preisEinheit, setPreisEinheit] = useState("");
  const [preisGueltigBis, setPreisGueltigBis] = useState("");
  const [aufschlag, setAufschlag] = useState("");
  const [verkaufspreis, setVerkaufspreis] = useState("");
  const [zuschlagModus, setZuschlagModus] = useState<ZuschlagModus>("regel");
  const [zuschlagRegelId, setZuschlagRegelId] = useState<number | "">("");
  const [zuschlagManuellPct, setZuschlagManuellPct] = useState("");
  const [zuschlagVk, setZuschlagVk] = useState("");
  const [importGeschuetzt, setImportGeschuetzt] = useState(true);
  const [preisGeschuetzt, setPreisGeschuetzt] = useState(true);
  const [softDelete, setSoftDelete] = useState(false);
  const deleteModeRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setJob(null);
    setSoftDelete(intent === "delete");
    deleteModeRef.current = intent === "delete";
    void getZuschlagRegeln()
      .then((r) => setRegeln(r))
      .catch(() => setRegeln([]));
  }, [open, intent]);

  const finishedRef = useRef(false);
  useEffect(() => {
    finishedRef.current = false;
  }, [open]);

  useEffect(() => {
    if (!job || job.status === "done" || job.status === "error") return;
    const t = window.setInterval(() => {
      void getArtikelBulkJob(job.id)
        .then((next) => {
          setJob(next);
          if (next.status === "done" && !finishedRef.current) {
            finishedRef.current = true;
            setSaving(false);
            const verb = deleteModeRef.current ? "gelöscht" : "aktualisiert";
            toast.success(
              `${next.updated.toLocaleString("de-DE")} Artikel ${verb}` +
                (next.skipped ? `, ${next.skipped.toLocaleString("de-DE")} übersprungen` : "") +
                ".",
            );
            onSaved();
          } else if (next.status === "error") {
            setError(next.error || "Massenbearbeitung fehlgeschlagen.");
            setSaving(false);
          }
        })
        .catch((err) => {
          setError(err instanceof ApiError ? err.message : "Status konnte nicht geladen werden.");
          setSaving(false);
        });
    }, 1000);
    return () => window.clearInterval(t);
  }, [job, onSaved]);

  function toggleField(key: FieldKey, checked: boolean) {
    setApply((prev) => ({ ...prev, [key]: checked }));
  }

  function buildUpdates(): ArtikelUpdatePayload | null {
    const updates: ArtikelUpdatePayload = {};
    // Artikelnummern müssen eindeutig sein – kein Massen-Setzen (Backend lehnt es ebenfalls ab).
    if (apply.name) updates.name = name.trim();
    if (apply.beschreibung) updates.beschreibung = beschreibung.trim();
    if (apply.einheit) updates.einheit = einheit.trim();
    if (apply.ean) updates.ean = ean.trim();
    if (apply.matchcode) updates.matchcode = matchcode.trim();
    if (apply.hersteller) updates.hersteller = hersteller.trim();
    if (apply.hersteller_nr) updates.hersteller_nr = herstellerNr.trim();
    if (apply.warengruppe) updates.warengruppe = warengruppe.trim();
    if (apply.lieferant) {
      updates.lieferant_name = lieferantName.trim();
      updates.lieferant_geschaeftspartner_id = lieferantGpId;
    }
    if (apply.preis_brutto) {
      const n = parseNum(preisBrutto);
      if (n == null) {
        setError("Bitte einen gültigen Bruttopreis eingeben.");
        return null;
      }
      updates.preis_brutto = n;
    }
    if (apply.rabatt) {
      const n = parseNum(rabatt);
      if (n == null) {
        setError("Bitte einen gültigen Rabatt eingeben.");
        return null;
      }
      updates.rabatt = n;
    }
    if (apply.preis_netto) {
      const n = parseNum(preisNetto);
      if (n == null) {
        setError("Bitte einen gültigen Nettopreis eingeben.");
        return null;
      }
      updates.preis_netto = n;
    }
    if (apply.preismenge) {
      const n = parseNum(preismenge);
      if (n == null) {
        setError("Bitte eine gültige Preismenge eingeben.");
        return null;
      }
      updates.preismenge = Math.round(n);
    }
    if (apply.preis_einheit) updates.preis_einheit = preisEinheit.trim();
    if (apply.preis_gueltig_bis) {
      updates.preis_gueltig_bis = preisGueltigBis.trim() || null;
    }
    if (apply.aufschlag) {
      const n = parseNum(aufschlag);
      if (n == null) {
        setError("Bitte einen gültigen Aufschlag eingeben.");
        return null;
      }
      updates.aufschlag = n;
    }
    if (apply.verkaufspreis) {
      const n = parseNum(verkaufspreis);
      if (n == null) {
        setError("Bitte einen gültigen Verkaufspreis eingeben.");
        return null;
      }
      updates.verkaufspreis = n;
    }
    return updates;
  }

  async function handleSave() {
    if (intent === "delete" || softDelete) {
      const n = selection.total.toLocaleString("de-DE");
      const ok = window.confirm(
        `${n} Artikel wirklich löschen?\n\nDie Artikel werden nur ausgeblendet. Stundennachweise und andere Belege, auf denen sie bereits verwendet werden, bleiben erhalten.`,
      );
      if (!ok) return;

      const useJob = selection.mode === "allMatching" || selection.total > SYNC_ID_LIMIT;
      deleteModeRef.current = true;
      setSaving(true);
      setError(null);
      try {
        if (!useJob && selection.mode === "ids") {
          const res = await bulkUpdateArtikel(selection.ids, {}, { softDelete: true });
          toast.success(
            res.updated === 1
              ? "1 Artikel gelöscht."
              : `${res.updated} Artikel gelöscht${res.skipped ? `, ${res.skipped} übersprungen` : ""}.`,
          );
          onSaved();
          onClose();
          return;
        }
        const started = await createArtikelBulkJob({
          filter: selection.mode === "allMatching" ? selection.filter : undefined,
          exclude_ids: selection.mode === "allMatching" ? selection.excludeIds : undefined,
          ids: selection.mode === "ids" ? selection.ids : undefined,
          soft_delete: true,
        });
        setJob(started);
      } catch (err) {
        setError(err instanceof ApiError ? err.message : "Löschen fehlgeschlagen.");
        setSaving(false);
      }
      return;
    }

    const updates = buildUpdates();
    if (updates == null) return;

    const schutz =
      apply.import_geschuetzt || apply.preis_geschuetzt
        ? {
            import_geschuetzt: apply.import_geschuetzt ? importGeschuetzt : null,
            preis_geschuetzt: apply.preis_geschuetzt ? preisGeschuetzt : null,
          }
        : undefined;

    let zuschlag:
      | {
          modus: ZuschlagModus;
          regel_id?: number | null;
          manuell_prozent?: number | null;
          verkaufspreis?: number | null;
        }
      | undefined;
    if (apply.zuschlag) {
      if (zuschlagModus === "regel") {
        if (zuschlagRegelId === "") {
          setError("Bitte eine Zuschlagsregel wählen.");
          return;
        }
        zuschlag = { modus: "regel", regel_id: Number(zuschlagRegelId) };
      } else if (zuschlagModus === "manuell_prozent") {
        const n = parseNum(zuschlagManuellPct);
        if (n == null) {
          setError("Bitte einen gültigen Zuschlag % eingeben.");
          return;
        }
        zuschlag = { modus: "manuell_prozent", manuell_prozent: n };
      } else {
        const n = parseNum(zuschlagVk);
        if (n == null) {
          setError("Bitte einen gültigen Verkaufspreis für den Zuschlag eingeben.");
          return;
        }
        zuschlag = { modus: "manuell_vk", verkaufspreis: n };
      }
    }

    if (Object.keys(updates).length === 0 && !schutz && !zuschlag) {
      setError("Bitte mindestens ein Feld auswählen und ausfüllen.");
      return;
    }

    const useJob =
      selection.mode === "allMatching" ||
      selection.total > SYNC_ID_LIMIT ||
      Boolean(schutz) ||
      Boolean(zuschlag);

    setSaving(true);
    setError(null);
    try {
      if (!useJob && selection.mode === "ids") {
        const res = await bulkUpdateArtikel(selection.ids, updates);
        toast.success(
          res.updated === 1
            ? "1 Artikel aktualisiert."
            : `${res.updated} Artikel aktualisiert${res.skipped ? `, ${res.skipped} übersprungen` : ""}.`,
        );
        onSaved();
        onClose();
        return;
      }

      const started = await createArtikelBulkJob({
        filter: selection.mode === "allMatching" ? selection.filter : undefined,
        exclude_ids: selection.mode === "allMatching" ? selection.excludeIds : undefined,
        ids: selection.mode === "ids" ? selection.ids : undefined,
        updates: Object.keys(updates).length ? updates : undefined,
        schutz,
        zuschlag,
      });
      setJob(started);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Massenbearbeitung fehlgeschlagen.");
      setSaving(false);
    }
  }

  const jobRunning = job != null && (job.status === "queued" || job.status === "running");
  const progressPct =
    job && job.total > 0 ? Math.min(100, Math.round((job.processed / job.total) * 100)) : 0;

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!jobRunning) onClose();
      }}
      title={intent === "delete" ? "Artikel löschen" : "Massenbearbeitung"}
      size={intent === "delete" ? "md" : "2xl"}
      bodyClassName="overflow-y-auto max-h-[75vh]"
    >
      <div className="space-y-4">
        <p className="text-sm text-label-secondary">
          {selection.total.toLocaleString("de-DE")} Artikel ausgewählt
          {selection.mode === "allMatching" ? " (alle aktuell gefilterten)" : ""}.
          {intent === "delete"
            ? " Die Artikel werden nur ausgeblendet. Stundennachweise und andere Belege bleiben erhalten."
            : " Nur angehakte Felder werden übernommen."}
        </p>

        {error ? <Alert variant="error">{error}</Alert> : null}

        {jobRunning || (job && job.status === "done") ? (
          <div className="space-y-2 rounded-ios border border-separator bg-fill-secondary/30 px-3 py-3">
            <p className="text-sm font-medium text-label">
              {job?.status === "done"
                ? "Fertig"
                : `Läuft … ${job?.processed.toLocaleString("de-DE") ?? 0} / ${job?.total.toLocaleString("de-DE") ?? 0}`}
            </p>
            <div className="h-2 overflow-hidden rounded-full bg-separator/50">
              <div
                className="h-full bg-accent transition-all"
                style={{ width: `${progressPct}%` }}
              />
            </div>
            {job?.status === "done" ? (
              <p className="text-xs text-label-secondary">
                {job.updated.toLocaleString("de-DE")} {deleteModeRef.current ? "gelöscht" : "aktualisiert"}
                {job.skipped ? `, ${job.skipped.toLocaleString("de-DE")} übersprungen` : ""}.
              </p>
            ) : null}
          </div>
        ) : (
          <div className="space-y-5">
            {intent === "delete" ? (
              <Alert variant="warning">
                {selection.total.toLocaleString("de-DE")} Artikel werden ausgeblendet, nicht
                endgültig entfernt. Bereits verwendete Positionen in Stundennachweisen und
                anderen Belegen bleiben unverändert nutzbar.
              </Alert>
            ) : (
            <>
            <section className="space-y-3">
              <h3 className="text-sm font-semibold text-label">Stammdaten</h3>
              <div className="grid gap-3 sm:grid-cols-2">
                <OptField checked={apply.name} onToggle={(v) => toggleField("name", v)}>
                  <TextInput label="Name setzen" value={name} onChange={(e) => setName(e.target.value)} disabled={!apply.name} />
                </OptField>
                <OptField checked={apply.einheit} onToggle={(v) => toggleField("einheit", v)}>
                  <UnitAutocompleteField
                    label="Einheit setzen"
                    value={einheit}
                    onChange={setEinheit}
                    disabled={!apply.einheit}
                  />
                </OptField>
                <OptField checked={apply.ean} onToggle={(v) => toggleField("ean", v)}>
                  <TextInput label="EAN setzen" value={ean} onChange={(e) => setEan(e.target.value)} disabled={!apply.ean} />
                </OptField>
                <OptField checked={apply.matchcode} onToggle={(v) => toggleField("matchcode", v)}>
                  <TextInput label="Matchcode setzen" value={matchcode} onChange={(e) => setMatchcode(e.target.value)} disabled={!apply.matchcode} />
                </OptField>
                <OptField checked={apply.warengruppe} onToggle={(v) => toggleField("warengruppe", v)}>
                  <TextInput label="Warengruppe setzen" value={warengruppe} onChange={(e) => setWarengruppe(e.target.value)} disabled={!apply.warengruppe} />
                </OptField>
                <OptField checked={apply.hersteller} onToggle={(v) => toggleField("hersteller", v)}>
                  <TextInput label="Hersteller setzen" value={hersteller} onChange={(e) => setHersteller(e.target.value)} disabled={!apply.hersteller} />
                </OptField>
                <OptField checked={apply.hersteller_nr} onToggle={(v) => toggleField("hersteller_nr", v)}>
                  <TextInput label="Hersteller-Nr. setzen" value={herstellerNr} onChange={(e) => setHerstellerNr(e.target.value)} disabled={!apply.hersteller_nr} />
                </OptField>
              </div>
              <OptField checked={apply.beschreibung} onToggle={(v) => toggleField("beschreibung", v)}>
                <label className="block text-sm">
                  <span className="mb-1 block font-medium text-label">Beschreibung setzen</span>
                  <textarea
                    className="min-h-[70px] w-full rounded-ios border border-separator bg-card px-3 py-2 text-sm disabled:opacity-50"
                    value={beschreibung}
                    onChange={(e) => setBeschreibung(e.target.value)}
                    disabled={!apply.beschreibung}
                  />
                </label>
              </OptField>
              <OptField checked={apply.lieferant} onToggle={(v) => toggleField("lieferant", v)}>
                <LieferantSearchField
                  label="Lieferant setzen"
                  value={lieferantName}
                  selectedGpId={lieferantGpId}
                  onChange={({ name: n, gpId }) => {
                    setLieferantName(n);
                    setLieferantGpId(gpId);
                  }}
                />
              </OptField>
            </section>

            <section className="space-y-3 border-t border-separator pt-4">
              <h3 className="text-sm font-semibold text-label">Einkaufspreis</h3>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                <OptField checked={apply.preis_brutto} onToggle={(v) => toggleField("preis_brutto", v)}>
                  <TextInput label="Bruttopreis (EK)" value={preisBrutto} onChange={(e) => setPreisBrutto(e.target.value)} disabled={!apply.preis_brutto} />
                </OptField>
                <OptField checked={apply.rabatt} onToggle={(v) => toggleField("rabatt", v)}>
                  <TextInput label="Rabatt %" value={rabatt} onChange={(e) => setRabatt(e.target.value)} disabled={!apply.rabatt} />
                </OptField>
                <OptField checked={apply.preis_netto} onToggle={(v) => toggleField("preis_netto", v)}>
                  <TextInput label="Nettopreis (EK)" value={preisNetto} onChange={(e) => setPreisNetto(e.target.value)} disabled={!apply.preis_netto} />
                </OptField>
                <OptField checked={apply.preismenge} onToggle={(v) => toggleField("preismenge", v)}>
                  <TextInput label="Preismenge" value={preismenge} onChange={(e) => setPreismenge(e.target.value)} disabled={!apply.preismenge} />
                </OptField>
                <OptField checked={apply.preis_einheit} onToggle={(v) => toggleField("preis_einheit", v)}>
                  <UnitAutocompleteField
                    label="Preiseinheit"
                    value={preisEinheit}
                    onChange={setPreisEinheit}
                    disabled={!apply.preis_einheit}
                  />
                </OptField>
                <OptField checked={apply.preis_gueltig_bis} onToggle={(v) => toggleField("preis_gueltig_bis", v)}>
                  <TextInput
                    label="Gültig bis"
                    type="date"
                    value={preisGueltigBis}
                    onChange={(e) => setPreisGueltigBis(e.target.value)}
                    disabled={!apply.preis_gueltig_bis}
                  />
                </OptField>
              </div>
            </section>

            <section className="space-y-3 border-t border-separator pt-4">
              <h3 className="text-sm font-semibold text-label">Verkauf</h3>
              <div className="grid gap-3 sm:grid-cols-2">
                <OptField checked={apply.aufschlag} onToggle={(v) => toggleField("aufschlag", v)}>
                  <TextInput label="Aufschlag % setzen" value={aufschlag} onChange={(e) => setAufschlag(e.target.value)} disabled={!apply.aufschlag} />
                </OptField>
                <OptField checked={apply.verkaufspreis} onToggle={(v) => toggleField("verkaufspreis", v)}>
                  <TextInput label="Verkaufspreis setzen" value={verkaufspreis} onChange={(e) => setVerkaufspreis(e.target.value)} disabled={!apply.verkaufspreis} />
                </OptField>
              </div>
              <OptField checked={apply.zuschlag} onToggle={(v) => toggleField("zuschlag", v)}>
                <div className="space-y-2">
                  <p className="font-medium text-label">Zuschlag (Regel / manuell) setzen</p>
                  <select
                    className="w-full rounded-ios border border-separator bg-card px-3 py-2 text-sm disabled:opacity-50"
                    value={zuschlagModus}
                    disabled={!apply.zuschlag}
                    onChange={(e) => setZuschlagModus(e.target.value as ZuschlagModus)}
                  >
                    <option value="regel">Regel</option>
                    <option value="manuell_prozent">Manuell %</option>
                    <option value="manuell_vk">Manuell VK</option>
                  </select>
                  {zuschlagModus === "regel" ? (
                    <select
                      className="w-full rounded-ios border border-separator bg-card px-3 py-2 text-sm disabled:opacity-50"
                      value={zuschlagRegelId}
                      disabled={!apply.zuschlag}
                      onChange={(e) =>
                        setZuschlagRegelId(e.target.value ? Number(e.target.value) : "")
                      }
                    >
                      <option value="">Regel wählen…</option>
                      {regeln.map((r) => (
                        <option key={r.id} value={r.id}>
                          ab {Number(r.ab_ek_netto).toLocaleString("de-DE")} € →{" "}
                          {Number(r.zuschlag_prozent).toLocaleString("de-DE")} %
                        </option>
                      ))}
                    </select>
                  ) : null}
                  {zuschlagModus === "manuell_prozent" ? (
                    <TextInput
                      label="Zuschlag %"
                      value={zuschlagManuellPct}
                      onChange={(e) => setZuschlagManuellPct(e.target.value)}
                      disabled={!apply.zuschlag}
                    />
                  ) : null}
                  {zuschlagModus === "manuell_vk" ? (
                    <TextInput
                      label="Verkaufspreis"
                      value={zuschlagVk}
                      onChange={(e) => setZuschlagVk(e.target.value)}
                      disabled={!apply.zuschlag}
                    />
                  ) : null}
                </div>
              </OptField>
            </section>

            <section className="space-y-3 border-t border-separator pt-4">
              <h3 className="text-sm font-semibold text-label">Löschen</h3>
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={softDelete}
                  onChange={(e) => setSoftDelete(e.target.checked)}
                />
                <span>
                  <span className="font-medium text-label">Ausgewählte Artikel löschen</span>
                  <span className="mt-1 block text-label-secondary">
                    Soft-Delete: die Artikel verschwinden aus Listen und der Suche. Stundennachweise
                    und andere Belege, auf denen ein Artikel schon verwendet wurde, bleiben
                    unverändert nutzbar. Andere Felder in diesem Dialog werden dabei nicht geändert.
                  </span>
                </span>
              </label>
              {softDelete ? (
                <Alert variant="warning">
                  {selection.total.toLocaleString("de-DE")} Artikel werden ausgeblendet, nicht
                  endgültig entfernt.
                </Alert>
              ) : null}
            </section>

            <section className="space-y-3 border-t border-separator pt-4">
              <h3 className="text-sm font-semibold text-label">Schutz</h3>
              <OptField
                checked={apply.import_geschuetzt}
                onToggle={(v) => toggleField("import_geschuetzt", v)}
              >
                <div className="flex flex-wrap items-center gap-3">
                  <span className="font-medium text-label">Importschutz setzen</span>
                  <select
                    className="rounded-ios border border-separator bg-card px-2 py-1.5 text-sm disabled:opacity-50"
                    value={importGeschuetzt ? "true" : "false"}
                    disabled={!apply.import_geschuetzt}
                    onChange={(e) => setImportGeschuetzt(e.target.value === "true")}
                  >
                    <option value="true">An</option>
                    <option value="false">Aus</option>
                  </select>
                </div>
              </OptField>
              <OptField
                checked={apply.preis_geschuetzt}
                onToggle={(v) => toggleField("preis_geschuetzt", v)}
              >
                <div className="flex flex-wrap items-center gap-3">
                  <span className="font-medium text-label">Preisschutz setzen</span>
                  <select
                    className="rounded-ios border border-separator bg-card px-2 py-1.5 text-sm disabled:opacity-50"
                    value={preisGeschuetzt ? "true" : "false"}
                    disabled={!apply.preis_geschuetzt}
                    onChange={(e) => setPreisGeschuetzt(e.target.value === "true")}
                  >
                    <option value="true">An</option>
                    <option value="false">Aus</option>
                  </select>
                </div>
              </OptField>
            </section>
            </>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2 border-t border-separator pt-4">
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={jobRunning}
          >
            {job?.status === "done" ? "Schließen" : "Abbrechen"}
          </Button>
          {!job || job.status === "error" ? (
            <Button
              variant={intent === "delete" || softDelete ? "danger" : "primary"}
              onClick={() => void handleSave()}
              disabled={saving || selection.total === 0}
            >
              {saving ? (
                <Spinner className="h-4 w-4" />
              ) : intent === "delete" || softDelete ? (
                "Löschen"
              ) : (
                "Anwenden"
              )}
            </Button>
          ) : null}
        </div>
      </div>
    </Dialog>
  );
}
